/**
 * Baseline Compliance Page — editorial brief layout (Phase 3 of the
 * Posture Pages Redesign).
 *
 * Visual targets:
 *   - .agents/Mockups/posture-pages-redesign/01-baseline-compliance.html
 *
 * Composition (top to bottom):
 *   1. PillarBrief — kicker, italic-mixed headline, deck, byline + ScoreBox
 *      (the four-bar Guardian Score composite, NOT a per-workload breakdown).
 *   2. PillarTabs — Drifts / PR Tracker / Export. Tab counts come from
 *      DashboardStats.activeDrifts and the existing PR query.
 *   3. Per-tab body:
 *      - Drifts:  KpiStrip + drift-trend chart + Section · 02 "Top items
 *                 worth your eye" (severity-ranked top-3 with frameworks ·
 *                 risk meta) + DriftManagementTab inside editorial chrome.
 *                 Top-items rows open the same detail drawer the table does.
 *                 Severity-tier filter chips wrap the table.
 *      - PR:      PrTrackerTab body wrapped in editorial chrome.
 *      - Export:  BaselineExportTab body wrapped in editorial chrome.
 *   4. FooterRail — three jump links back to Command Center + the other
 *      two pillars.
 *
 * Hard-preserved (regression surface): drift status / workload / severity
 * filters, drift detail drawer, approve/waive/commit actions, PR links,
 * CSV/HTML export downloads, tab URL sync (?tab=…), tenant context auto-sync
 * from the sidebar, "Start new scan" controls.
 *
 * Defer audit (per posture-pages-redesign.md §2.1):
 *   - Per-workload baseline contribution → swapped for the four-component
 *     Guardian Score composite (real values, no fabricated weighting).
 *   - "21 April" anomaly marker on the trend chart → no anomaly model exists.
 *   - "Lifts baseline by ~N pts" sub-clauses on priority rows → no impact
 *     model. The Section · 02 rows surface real fields only (frameworks
 *     touched + risk score) instead of a fabricated baseline lift.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts'
import type { TooltipProps } from 'recharts'
import { format } from 'date-fns'

import { api } from '@/lib/api'
import { useTenantContext } from '@/hooks/useTenantContext'
import { useTheme } from '@/context/ThemeContext'
import { getThemeColors } from '@/lib/themeColors'
import {
  calculateStarredBpaScore,
  useComplianceScores,
} from '@/hooks/useComplianceData'
import { useComplianceBrief } from '@/hooks/useComplianceBrief'
import { consecutiveImprovementDays, summarizeDriftTrend } from '@/lib/derive-trend-stats'
import { getWorkloadForDriftComponent } from '@/lib/workloadMapping'
import { type ConfigurationDrift, DriftSeverity, type M365Workload } from '@/types'

import PillarBrief from '@/components/PostureBrief/PillarBrief'
import PillarTabs, {
  usePillarTab,
  type TabConfig,
} from '@/components/PostureBrief/PillarTabs'
import KpiStrip, { type KpiCell } from '@/components/PostureBrief/KpiStrip'
import FooterRail from '@/components/PostureBrief/FooterRail'
import {
  SectionEyebrow,
  SectionHeadline,
} from '@/components/PostureBrief/SectionHeadline'

import {
  DriftManagementTab,
  PrTrackerTab,
  BaselineExportTab,
} from '@/components/BaselineCompliance'

type TabKey = 'drifts' | 'pr-tracker' | 'export'

// Drifts that still need a decision or are mid-review — i.e. "active".
// Lifted from PillarCompliance.tsx so the same definition powers the page
// summary list and the triptych.
const ACTIVE_DRIFT_STATUSES = 'New,Pending,PendingApproval,AwaitingRemediation'

// Activity types treated as "resolved" for the 30d KPI. Same set used by the
// triptych pillar so the two surfaces don't disagree.
const RESOLVED_ACTIVITY_TYPES = ['drift_resolved', 'drift_remediated'] as const

const SEVERITY_RANK: Record<DriftSeverity, number> = {
  [DriftSeverity.Critical]: 0,
  [DriftSeverity.High]:     1,
  [DriftSeverity.Medium]:   2,
  [DriftSeverity.Low]:      3,
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatAge(iso?: string | null): string {
  if (!iso) return 'just now'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return 'just now'
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000))
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function pluralize(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function ConfigurationDrift() {
  const [searchParams] = useSearchParams()
  const { defaultTenantId, isGlobalAdmin, isLoading: isLoadingTenant, userTenants } =
    useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)

  // Subscribe to theme so chart colors track dark/light toggles.
  useTheme()
  const tc = getThemeColors()

  // Initialize selectedTenantId from context once loaded. Same precedence as
  // the legacy page so deep-linked ?tenant=… continues to override.
  useEffect(() => {
    if (isLoadingTenant) return
    const tenantParam = searchParams.get('tenant')
    if (tenantParam) {
      setSelectedTenantId(tenantParam)
      return
    }
    if (defaultTenantId) {
      setSelectedTenantId(defaultTenantId)
    } else if (!isGlobalAdmin && userTenants.length > 0) {
      setSelectedTenantId(userTenants[0].tenantId)
    }
  }, [isLoadingTenant, defaultTenantId, isGlobalAdmin, userTenants, searchParams])

  /* ---------------- Data queries ---------------- */

  // DashboardStats: drives compliance score, severity counts, drift trends,
  // active drift count, recent activity. Same key as the Command Center to
  // share cache.
  const { data: dashboardStats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () =>
      selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  // Active drifts (top-3 list + drifting-workload extraction for the brief).
  const { data: activeDriftsData } = useQuery({
    queryKey: ['baseline-compliance-active-drifts', selectedTenantId],
    queryFn: () =>
      api.getConfigurationDrifts({
        tenantId: selectedTenantId,
        status: ACTIVE_DRIFT_STATUSES,
        page: 1,
        limit: 25,
      }),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
  })

  // Pull-request count for the PR Tracker tab badge — same endpoint shape
  // PrTrackerTab uses internally; we just need totalCount here.
  const { data: prCountData } = useQuery({
    queryKey: ['devopsPullRequests-count', selectedTenantId],
    queryFn: () =>
      api.getDevOpsPullRequests({
        tenantId: selectedTenantId!,
        page: 1,
        pageSize: 1,
      }),
    enabled: !!selectedTenantId,
    staleTime: 30 * 1000,
  })

  // Score-history feed for the 30-day score delta (drives the score-box pill
  // and the headline modifier sign). Slow-changing → 5 minute stale.
  const { data: scoreHistory } = useQuery({
    queryKey: ['compliance-score-history-30d', selectedTenantId],
    queryFn: () => api.getComplianceScoreHistory(selectedTenantId!, 30),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
  })

  // Guardian Score components (Baseline 20 / BPA 30 / MFA 20 / Secure Score 30).
  // Wired identically to Dashboard.tsx so the score box renders the same
  // composite the Command Center shows.
  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: userInsights, isLoading: userInsightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores, isLoading: complianceScoresLoading } =
    useComplianceScores(selectedTenantId)

  // Mirrors Dashboard.tsx — uses `weightedScore` so the composite-bar tile
  // matches the BPA pillar's hero metric (which sources `weightedScore` via
  // `transformToDisplayFrameworks`).
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  /* ---------------- Derivations ---------------- */

  // Stabilise the array reference so the useMemo hooks downstream
  // (consecutiveImprovementDays, summarizeDriftTrend) don't re-fire on every
  // render. Without this the `?? []` fallback creates a fresh literal on each
  // call and the deps array sees a different identity every time.
  const driftTrends = useMemo(
    () => dashboardStats?.driftTrends ?? [],
    [dashboardStats?.driftTrends],
  )
  const recentActivity = dashboardStats?.recentActivity ?? []
  const activeDriftCount = dashboardStats?.activeDrifts ?? 0
  const pendingCommits = dashboardStats?.pendingProcessing ?? 0
  const resolved30d = recentActivity.filter(a =>
    (RESOLVED_ACTIVITY_TYPES as readonly string[]).includes(a.type),
  ).length

  const sev = dashboardStats?.issuesBySeverity
  const critical = sev?.critical ?? 0
  const high = sev?.high ?? 0
  const medium = sev?.medium ?? 0
  const low = sev?.low ?? 0

  // 30-day pillar score delta — first → last point of the 30d overall
  // history, falling back to 0 when history is too short. Drives the
  // ScoreBox pill via the brief.
  // `getComplianceScoreHistory` already normalises to a sorted
  // `ComplianceTrendPoint[]` (oldest → newest).
  const scoreDeltaValue = useMemo(() => {
    if (!Array.isArray(scoreHistory) || scoreHistory.length < 2) return 0
    const first = scoreHistory[0]?.overallScore ?? null
    const last = scoreHistory[scoreHistory.length - 1]?.overallScore ?? null
    if (first == null || last == null) return 0
    return Math.round((last - first) * 10) / 10
  }, [scoreHistory])

  // Top-3 active drifts: severity-rank then most-recent. Same logic as
  // PillarCompliance — keeping a single behavior across surfaces.
  const topDrifts = useMemo<ConfigurationDrift[]>(() => {
    const items = activeDriftsData?.items ?? []
    return [...items]
      .sort((a, b) => {
        const sevDelta = (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9)
        if (sevDelta !== 0) return sevDelta
        return new Date(b.detectedAt).getTime() - new Date(a.detectedAt).getTime()
      })
      .slice(0, 3)
  }, [activeDriftsData?.items])

  // Distinct workloads with active drifts — feeds the brief weak-link clause.
  // Maps each drift's M365DSC component prefix to the canonical workload
  // bucket (Identity / Exchange / Teams / SharePoint / Defender) and dedupes,
  // so the brief names real workloads instead of resource names. `Other` is
  // dropped — naming it as a weak-link subject reads as filler.
  const driftingWorkloads = useMemo(() => {
    const items = activeDriftsData?.items ?? []
    const set = new Set<M365Workload>()
    for (const d of items) {
      const wl = getWorkloadForDriftComponent(d.component)
      if (wl !== 'Other') set.add(wl)
    }
    return Array.from(set)
  }, [activeDriftsData?.items])

  // Streak helper. Returns null when data is too short — drop the statement
  // entirely in that case (editorial rule from §3 / §2.1).
  const improvementDays = useMemo(
    () => consecutiveImprovementDays(driftTrends),
    [driftTrends],
  )

  // Brief composition. Score = REAL pillar percentage (DashboardStats), delta
  // = 30d derived above, workload list flows into the weak-link clause,
  // critical count drives the count-driven fallback.
  const brief = useComplianceBrief({
    tenantId: selectedTenantId,
    score: dashboardStats?.complianceScore?.percentage ?? null,
    deltas: { score: scoreDeltaValue, days: 30 },
    criticalCount: critical,
    driftingWorkloads,
    topAction: topDrifts[0]
      ? {
          pillar: 'compliance',
          title: `${topDrifts[0].component} · ${topDrifts[0].setting}`,
          impact:
            topDrifts[0].severity === DriftSeverity.Critical
              ? 'crit'
              : topDrifts[0].severity === DriftSeverity.High
                ? 'high'
                : 'med',
        }
      : undefined,
  })

  // Guardian Score composite — same four bars the Command Center shows. We
  // intentionally use the SAME bar mix on every Posture page (per §2.1
  // "Replace with the four-component composite from the Command Center").
  const compositeBars = useMemo(
    () => [
      {
        label: 'Baseline drift',
        weight: '20%',
        value: Math.round(dashboardStats?.complianceScore?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
      {
        label: 'Best practice',
        weight: '30%',
        value: Math.round(bpaStarredScore ?? 0),
        fillClass: 'bg-secondaryBlue-300',
      },
      {
        label: 'MFA adoption',
        weight: '20%',
        value: Math.round(userInsights?.mfaMemberAdoptionPercentage ?? 0),
        fillClass: 'bg-secondarySand-300',
      },
      {
        label: 'M365 Secure Score',
        weight: '30%',
        value: Math.round(secureScoreSummary?.score?.percentage ?? 0),
        fillClass: 'bg-primary-500',
      },
    ],
    [
      dashboardStats?.complianceScore?.percentage,
      bpaStarredScore,
      userInsights?.mfaMemberAdoptionPercentage,
      secureScoreSummary?.score?.percentage,
    ],
  )

  const briefLoading =
    statsLoading || secureScoreLoading || userInsightsLoading || complianceScoresLoading

  /* ---------------- Tabs ---------------- */

  const tabs = useMemo<ReadonlyArray<TabConfig<TabKey>>>(
    () => [
      { id: 'drifts',     label: 'Drifts',     badge: activeDriftCount },
      { id: 'pr-tracker', label: 'PR Tracker', badge: prCountData?.totalCount ?? 0 },
      { id: 'export',     label: 'Export' },
    ],
    [activeDriftCount, prCountData?.totalCount],
  )

  // The page composes a single source of truth for the active tab. PillarTabs
  // also calls usePillarTab internally for the URL sync; the validIds list is
  // identical so both stay aligned.
  const [activeTab] = usePillarTab(
    tabs.map(t => t.id),
    'drifts',
  )

  // Hash-deep-link from the Operations heatmap (`#full-queue`). The section
  // only mounts on the Drifts tab and the brief above it shifts vertical
  // position once data lands, so we re-scroll after `briefLoading` flips
  // false to land on the right pixel.
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (window.location.hash !== '#full-queue') return
    if (activeTab !== 'drifts') return
    if (briefLoading) return
    const id = window.requestAnimationFrame(() => {
      document.getElementById('full-queue')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
    return () => window.cancelAnimationFrame(id)
  }, [activeTab, briefLoading])

  /* ---------------- Render ---------------- */

  return (
    <div className="mx-auto max-w-[1320px]">
      <PillarBrief
        brief={brief}
        score={dashboardStats?.complianceScore?.percentage ?? null}
        components={compositeBars}
        tone="compliance"
        isLoading={briefLoading}
        scoreLabel="Baseline Compliance Score"
      />

      <PillarTabs
        tabs={tabs}
        defaultTab="drifts"
        tone="compliance"
        ariaLabel="Baseline compliance view"
      />

      {activeTab === 'drifts' && (
        <DriftsTabPanel
          tenantId={selectedTenantId}
          activeDriftCount={activeDriftCount}
          pendingCommits={pendingCommits}
          resolved30d={resolved30d}
          driftTrends={driftTrends}
          improvementDays={improvementDays}
          topDrifts={topDrifts}
          chartColor={tc.primary}
          severityCounts={{ critical, high, medium, low }}
        />
      )}

      {activeTab === 'pr-tracker' && (
        <EditorialPanel
          eyebrow="Section · 01"
          headline="Baseline updates "
          headlineEm="in flight."
          sub="Pull requests opened from approved drifts. Sync, expand, and follow each one through to merge."
        >
          <PrTrackerTab tenantId={selectedTenantId} />
        </EditorialPanel>
      )}

      {activeTab === 'export' && (
        <EditorialPanel
          eyebrow="Section · 01"
          headline="Take the record "
          headlineEm="with you."
          sub="Download the active drift queue or the full audit history as CSV or HTML."
        >
          <BaselineExportTab tenantId={selectedTenantId} />
        </EditorialPanel>
      )}

      <FooterRail currentTone="compliance" />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Drifts tab body                                                            */
/* -------------------------------------------------------------------------- */

interface DriftsTabPanelProps {
  tenantId: string | undefined
  activeDriftCount: number
  pendingCommits: number
  resolved30d: number
  driftTrends: { date: string; critical: number; high: number; medium: number; low: number; info: number }[]
  improvementDays: number | null
  topDrifts: ConfigurationDrift[]
  chartColor: string
  severityCounts: { critical: number; high: number; medium: number; low: number }
}

function DriftsTabPanel({
  tenantId,
  activeDriftCount,
  pendingCommits,
  resolved30d,
  driftTrends,
  improvementDays,
  topDrifts,
  chartColor,
  severityCounts,
}: DriftsTabPanelProps) {
  // Drawer selection lives at this level so Section · 02 "Top items" rows and
  // the Section · 03 full-queue table both open the same DriftDetailDrawer
  // instance owned by DriftManagementTab.
  const [selectedDriftId, setSelectedDriftId] = useState<string | null>(null)

  // Sparkline + 30d series. The series data is the per-day total open-drift
  // count (sum of severity buckets) — same shape PillarCompliance uses.
  const sparkSeries = useMemo(
    () =>
      driftTrends.map(t => ({
        date: t.date,
        value:
          (t.critical ?? 0) +
          (t.high ?? 0) +
          (t.medium ?? 0) +
          (t.low ?? 0) +
          (t.info ?? 0),
      })),
    [driftTrends],
  )

  // Editorial verdict + window/week totals for the chart headline. Returns
  // null when the trend series is empty, so the chart caption falls back to
  // the existing "Trend data will appear once a few daily scans have run."
  // empty state instead of pretending the math worked.
  const trendSummary = useMemo(() => summarizeDriftTrend(driftTrends), [driftTrends])

  const kpis: KpiCell[] = useMemo(() => {
    // Trend copy for "Active drifts" — drop the streak sentence entirely
    // when consecutiveImprovementDays returns null (data too short).
    const activeTrend = improvementDays
      ? `Awaiting your decision · **${improvementDays}-day** non-increasing streak`
      : 'Awaiting your decision'

    return [
      {
        id: 'active',
        numeral: String(activeDriftCount).padStart(2, '0'),
        label: 'Active drifts',
        stat: pluralize(activeDriftCount, 'One open item', `${activeDriftCount} open items`),
        trend: activeTrend,
        severity: activeDriftCount === 0
          ? 'tone'
          : activeDriftCount > 10
            ? 'crit'
            : activeDriftCount > 5
              ? 'warn'
              : 'tone',
        spark: sparkSeries.length > 0 ? <KpiSparkline series={sparkSeries} color={chartColor} /> : null,
      },
      {
        id: 'pending',
        numeral: String(pendingCommits).padStart(2, '0'),
        label: 'Pending commits',
        stat: pendingCommits === 0
          ? 'Nothing waiting to land'
          : 'Approved · ready to land',
        trend: pendingCommits > 0
          ? `**${pendingCommits}** approved ${pluralize(pendingCommits, 'change', 'changes')} ready to commit`
          : undefined,
      },
      {
        id: 'resolved',
        numeral: String(resolved30d).padStart(2, '0'),
        label: 'Resolved · 30d',
        stat: resolved30d === 0
          ? 'Nothing closed in the last 30 days'
          : 'Closed by your team',
        trend: undefined,
      },
    ]
  }, [activeDriftCount, pendingCommits, resolved30d, improvementDays, sparkSeries, chartColor])

  return (
    <div id="panel-drifts" role="tabpanel" aria-labelledby="tab-drifts">
      <KpiStrip tone="compliance" cells={kpis} />

      {/* Section · 01 — Drift trend, 30 days. Editorial chrome on page canvas;
          chart sits in a paper card with a thin rule border.

          Headline names the verdict (Voice rule #1 — ≤6 words), the deck
          carries the actual counts so the chart can never go silent on
          numbers again. Per-point Tooltip surfaces the absolute count on
          hover for explore-mode. Both numbers come from the existing
          DashboardStats.driftTrends payload — no new endpoint. */}
      <section data-tone="compliance" className="mt-12">
        <SectionEyebrow text="Section · 01 · Drift trend" />
        <SectionHeadline level="page">
          {trendSummary
            ? renderTrendHeadline(trendSummary.verdict)
            : <>Drift trend, <em>30 days.</em></>}
        </SectionHeadline>
        <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
          {trendSummary ? (
            <>
              <span className="font-mono tabular-nums not-italic font-semibold text-[color:var(--color-navy,#212938)]">
                {trendSummary.windowTotal}
              </span>{' '}
              {pluralize(trendSummary.windowTotal, 'drift', 'drifts')} opened across the last 30 days.{' '}
              {activeDriftCount > 0 ? (
                <>
                  <span className="font-mono tabular-nums not-italic font-semibold text-[color:var(--color-navy,#212938)]">
                    {activeDriftCount}
                  </span>{' '}
                  {pluralize(activeDriftCount, 'is', 'are')} still open today.
                </>
              ) : (
                <>Nothing is open today.</>
              )}
              {trendSummary.direction != null && (
                <>
                  {' '}
                  {renderWeekOverWeek(trendSummary)}
                </>
              )}
            </>
          ) : (
            <>New drifts opened per day, summed across workloads.</>
          )}
        </p>
        <div className="mt-6">
          <div
            className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] p-3"
            style={{ height: 220 }}
          >
            {sparkSeries.length === 0 ? (
              <div className="flex h-full items-center justify-center text-[12px] italic text-[color:var(--color-mute,#6B7280)]">
                Trend data will appear once a few daily scans have run.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sparkSeries} margin={{ top: 12, right: 8, left: 8, bottom: 4 }}>
                  <defs>
                    <linearGradient id="baseline-trend-grad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%"   stopColor={chartColor} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  {/* Hover tooltip shows the absolute count for the day under
                      the cursor — "May 9 · 18 drifts opened". Solves the
                      "chart has no reference to the number of drifts"
                      complaint at the explore layer (the headline + deck
                      solves it at the at-a-glance layer). */}
                  <Tooltip
                    cursor={{ stroke: chartColor, strokeOpacity: 0.25, strokeWidth: 1 }}
                    content={<DriftTrendTooltip />}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={chartColor}
                    strokeWidth={2.5}
                    fill="url(#baseline-trend-grad)"
                    fillOpacity={1}
                    isAnimationActive
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </section>

      {/* Section · 02 — Top items worth your eye. Stacked beneath the trend
          chart on the editorial canvas (no card wrapper). */}
      <section data-tone="compliance" className="mt-12">
        <SectionEyebrow text="Section · 02 · Top items worth your eye" />
        <SectionHeadline level="page">
          Top items <em>worth your eye.</em>
        </SectionHeadline>
        <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
          The few drifts most likely to move the score if you close them this week.
        </p>
        <div className="mt-6 flex flex-col">
          {topDrifts.length === 0 ? (
            <p className="py-4 text-[14px] italic leading-[1.55] text-[color:var(--color-mute,#6B7280)]">
              Nothing needs your attention right now. The baseline is steady.
            </p>
          ) : (
            topDrifts.map(drift => (
              <TopDriftRow
                key={drift.id}
                drift={drift}
                onSelect={setSelectedDriftId}
              />
            ))
          )}
        </div>
      </section>

      {/* The full queue. Editorial chrome (eyebrow + headline) wraps the
          existing DriftManagementTab body unchanged so status / workload /
          severity filters, bulk actions, the detail drawer, and modal flows
          all keep working. The severity dropdown inside DriftFilters has
          been replaced with chips driven by DashboardStats.issuesBySeverity
          (posture-pages-redesign §6) — that's the canonical filter UI.
          data-tone="compliance" stays on the section so the [&_em] accent
          inside SectionHeadline resolves to --tone-accent-deep (the page
          root carries no data-tone ancestor). DriftManagementTab is run in
          controlled-selection mode so Section · 02 rows can open the same
          drawer instance instead of a parallel one. */}
      <section id="full-queue" data-tone="compliance" className="mt-12 scroll-mt-24">
        <SectionEyebrow text="Section · 03 · The full queue" />
        <SectionHeadline level="page">
          The full <em>queue.</em>
        </SectionHeadline>
        <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
          Open drifts ranked by severity.{' '}
          <span className="font-mono tabular-nums not-italic">{activeDriftCount}</span> active,{' '}
          <span className="font-mono tabular-nums not-italic">{severityCounts.critical}</span> critical,{' '}
          <span className="font-mono tabular-nums not-italic">{severityCounts.high}</span> high.
        </p>
        <div className="mt-6">
          <DriftManagementTab
            tenantId={tenantId}
            selectedDriftId={selectedDriftId}
            onSelectDrift={setSelectedDriftId}
          />
        </div>
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Render a verdict headline with the final word/clause italicized as the
 * `em` accent — matching the editorial pattern the rest of the page uses
 * (`Drift trend, <em>30 days.</em>`). The verdicts are short enough that the
 * split point is always "first clause" + final-word/period; this keeps the
 * rule simple instead of carrying around a parsed tuple from the helper.
 */
function renderTrendHeadline(verdict: string): React.ReactNode {
  // Verdicts come from `summarizeDriftTrend` — a fixed set, so we hand-pick
  // each split rather than running a regex. Falls back to italicizing the
  // whole verdict if we ever ship a new one without updating this map.
  switch (verdict) {
    case 'Drift is easing.':
      return <>Drift is <em>easing.</em></>
    case 'Drift is climbing.':
      return <>Drift is <em>climbing.</em></>
    case 'Drift is steady.':
      return <>Drift is <em>steady.</em></>
    case 'No drift opened this month.':
      return <>No drift opened <em>this month.</em></>
    case 'Drift, last 30 days.':
      return <>Drift, <em>last 30 days.</em></>
    default:
      return <em>{verdict}</em>
  }
}

/**
 * Week-over-week clause for the deck. Reads "Down from 23 last week." or
 * "Up from 9 last week." — sentence-cased fragment to chain onto the deck
 * sentence. Returns null when there's no direction to talk about (handled
 * by the caller).
 */
function renderWeekOverWeek(
  summary: { direction: 'rising' | 'easing' | 'flat' | null; recentTotal: number; priorTotal: number },
): React.ReactNode {
  if (summary.direction === null) return null

  // Flat WoW with zero prior reads as "Holding flat this week."
  if (summary.direction === 'flat') {
    return <span className="not-italic">Holding flat versus last week.</span>
  }

  const verb = summary.direction === 'easing' ? 'Down from' : 'Up from'
  return (
    <span className="not-italic">
      {verb}{' '}
      <span className="font-mono tabular-nums font-semibold text-[color:var(--color-navy,#212938)]">
        {summary.priorTotal}
      </span>{' '}
      last week.
    </span>
  )
}

/**
 * Custom Recharts tooltip for the drift-trend chart. Shows the absolute count
 * for the hovered day so the chart has the missing reference to "how many".
 * Styled to match the editorial paper palette (cream surface, navy ink, rule
 * border) instead of the dark dashboard tooltip used elsewhere.
 */
function DriftTrendTooltip({
  active,
  payload,
}: TooltipProps<number, string>) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0]?.payload as { date?: string; value?: number } | undefined
  if (!point || typeof point.value !== 'number') return null

  // Parse the ISO date defensively — the API hands back YYYY-MM-DD strings
  // today, but we don't want a malformed point to crash the tooltip.
  let dateLabel = point.date ?? ''
  if (point.date) {
    const parsed = new Date(point.date)
    if (!Number.isNaN(parsed.getTime())) {
      dateLabel = format(parsed, 'MMM d')
    }
  }

  return (
    <div className="rounded-[4px] border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-paper,#fff)] px-3 py-2 shadow-sm">
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--color-mute,#6B7280)]">
        {dateLabel}
      </div>
      <div className="mt-0.5 text-[14px] font-semibold text-[color:var(--color-navy,#212938)]">
        <span className="font-mono tabular-nums">{point.value}</span>{' '}
        <span className="font-normal italic text-[color:var(--color-mute,#6B7280)]">
          {point.value === 1 ? 'drift opened' : 'drifts opened'}
        </span>
      </div>
    </div>
  )
}

function KpiSparkline({
  series,
  color,
}: {
  series: { date: string; value: number }[]
  color: string
}) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={series} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="kpi-spark-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={color} stopOpacity={0.4} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill="url(#kpi-spark-grad)"
          fillOpacity={1}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

const SEVERITY_BAR_COLOR: Record<DriftSeverity, string> = {
  [DriftSeverity.Critical]: '#C84B4B',
  [DriftSeverity.High]:     'rgb(var(--color-secondary-sand-500))',
  [DriftSeverity.Medium]:   'rgb(var(--color-secondary-blue-500))',
  [DriftSeverity.Low]:      'rgb(var(--color-primary-500))',
}

// Tier mapping for the 0–1 riskScore. Breakpoints match DriftDetails /
// DriftDetailDrawer so the same score lands in the same tier across surfaces.
type RiskTier = 'critical' | 'high' | 'med' | 'low'

const RISK_TIER_LABEL: Record<RiskTier, string> = {
  critical: 'Critical',
  high:     'High',
  med:      'Medium',
  low:      'Low',
}

const RISK_TIER_COLOR: Record<RiskTier, string> = {
  critical: '#C84B4B',
  high:     'rgb(var(--color-secondary-sand-500))',
  med:      'rgb(var(--color-secondary-blue-500))',
  low:      'rgb(var(--color-primary-500))',
}

function toRiskTier(score: number): RiskTier {
  if (score >= 0.8) return 'critical'
  if (score >= 0.6) return 'high'
  if (score >= 0.4) return 'med'
  return 'low'
}

function TopDriftRow({
  drift,
  onSelect,
}: {
  drift: ConfigurationDrift
  onSelect: (id: string) => void
}) {
  const frameworkPill = drift.complianceFrameworkImpact?.[0]?.controlId
    ? `${drift.complianceFrameworkImpact[0].frameworkName} ${drift.complianceFrameworkImpact[0].controlId}`
    : null

  const workloadPill = drift.component
  const resourcePill =
    drift.componentName && drift.componentName !== drift.component
      ? drift.componentName
      : null

  // "Frameworks · risk" meta surfaces only real fields — no fabricated
  // baseline-lift number. Either half can be absent; if both are, the cell
  // is silent.
  const frameworksTouched = drift.complianceFrameworkImpact?.length ?? 0
  const frameworksLabel =
    frameworksTouched >= 1
      ? `${frameworksTouched} ${pluralize(frameworksTouched, 'control', 'controls')}`
      : null
  const riskScore = drift.riskScore
  const hasRisk = riskScore != null
  const tier = hasRisk ? toRiskTier(riskScore) : null
  const riskInteger = hasRisk ? Math.round(riskScore * 100) : null

  const handleActivate = () => onSelect(drift.id)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleActivate()
        }
      }}
      className="grid cursor-pointer grid-cols-[4px_1fr_auto_auto] items-center gap-4 border-b border-[color:var(--color-rule,#E6E1D5)] py-3.5 last:border-b-0 hover:bg-[color:var(--color-cream,#FBF8F2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-navy,#212938)] focus-visible:ring-offset-2"
    >
      <span
        aria-hidden
        className="block self-stretch rounded-full"
        style={{ background: SEVERITY_BAR_COLOR[drift.severity] ?? 'rgb(var(--color-mute))' }}
      />
      <div className="min-w-0">
        <div className="text-[14px] font-bold leading-tight text-[color:var(--color-navy,#212938)]">
          {drift.component} · {drift.setting}
        </div>
        {drift.humanReadableDescription && (
          <div className="mt-0.5 line-clamp-1 text-[12px] italic leading-snug text-[color:var(--color-mute,#6B7280)]">
            {drift.humanReadableDescription}
          </div>
        )}
        <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
          {workloadPill && (
            <span className="inline-block rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] px-2 py-0.5">
              {workloadPill}
            </span>
          )}
          {resourcePill && (
            <span className="inline-block max-w-[260px] truncate rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] px-2 py-0.5 normal-case tracking-normal">
              {resourcePill}
            </span>
          )}
          {frameworkPill && (
            <span className="inline-block rounded-full border border-[color:var(--color-rule,#E6E1D5)] bg-[color:var(--color-cream,#FBF8F2)] px-2 py-0.5">
              {frameworkPill}
            </span>
          )}
        </div>
      </div>
      <span className="self-center whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-mute,#6B7280)]">
        {frameworksLabel && (
          <span className="font-mono tabular-nums not-italic">{frameworksLabel}</span>
        )}
        {frameworksLabel && hasRisk && <span> · </span>}
        {hasRisk && tier && (
          <>
            <span style={{ color: RISK_TIER_COLOR[tier] }}>{RISK_TIER_LABEL[tier]}</span>
            {' · '}
            <span className="font-mono tabular-nums not-italic">{riskInteger}</span>
          </>
        )}
      </span>
      <span className="self-center whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--color-faint,#9CA3AF)]">
        {formatAge(drift.detectedAt)}
      </span>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Editorial wrapper for the PR Tracker / Export tabs                         */
/* -------------------------------------------------------------------------- */

interface EditorialPanelProps {
  eyebrow: string
  headline: string
  headlineEm: string
  sub: string
  children: React.ReactNode
}

function EditorialPanel({
  eyebrow,
  headline,
  headlineEm,
  sub,
  children,
}: EditorialPanelProps) {
  return (
    <section data-tone="compliance" className="mt-12">
      <SectionEyebrow text={eyebrow} />
      <SectionHeadline level="page">
        {headline}<em>{headlineEm}</em>
      </SectionHeadline>
      <p className="mt-2 max-w-2xl text-[14px] italic text-[color:var(--color-mute,#6B7280)]">
        {sub}
      </p>
      <div className="mt-6">{children}</div>
    </section>
  )
}
